/*
 * Version: 1.2.0
 * Date: 2025-01-08 16:40:00
 * Description: Calibration Certificate Tracker - Main Application
 * Changes:
 * - v1.2.0: Added certificate/equipment add forms, improved error handling
 * - v1.1.0: Added serial-centric view, user permissions
 * - v1.0.0: Initial version
 */

import React, { useState, useEffect } from 'react';
import { Plus, Search, Download, Upload, Lock, History, ArrowLeft, Shield, Eye, Edit2, Save, Trash2, X } from 'lucide-react';

const API_URL = 'https://calibration-management.v-kit.com/api';

const CalibrationTracker = () => {
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [certificates, setCertificates] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentData, setEquipmentData] = useState({});
  const [equipmentSchemas, setEquipmentSchemas] = useState({});
  const [viewMode, setViewMode] = useState('serial'); // 'serial' or 'table'
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAddCertificate, setShowAddCertificate] = useState(false);
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [showAddType, setShowAddType] = useState(false);
  const [newType, setNewType] = useState('');
  const [newCertificate, setNewCertificate] = useState({
    tool: '',
    serial_number: '',
    cal_date: '',
    exp_months: '12',
    result: 'PASS'
  });
  const [newEquipment, setNewEquipment] = useState({
    customer: '',
    serial_number: '',
    model: 'VKIT-LFM',
    recal: 'Y'
  });

  const LOCK_DAYS = 30;

  useEffect(() => {
    const savedUser = localStorage.getItem('calibration_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      loadData();
    }
  }, []);

  const api = async (endpoint, method = 'GET', data = null) => {
    const token = user?.token || localStorage.getItem('calibration_user_token');
    
    console.log('API call:', { endpoint, method, hasToken: !!token }); // Debug log
    
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token || ''
      }
    };
    if (data) options.body = JSON.stringify(data);
    
    try {
      const response = await fetch(`${API_URL}${endpoint}`, options);
      const result = await response.json();
      
      console.log('API response:', { endpoint, status: response.status, result }); // Debug log
      
      if (!response.ok) {
        console.error('API Error:', result);
        if (response.status === 401) {
          // Token expired or invalid
          handleLogout();
        }
        throw new Error(result.message || 'API request failed');
      }
      
      return result;
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      
      const loginURL = `${API_URL}/auth/login.php`;
      console.log('Attempting login to:', loginURL);
      console.log('Login data:', loginForm);
      
      const response = await fetch(loginURL, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(loginForm)
      });
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      console.log('Response content-type:', response.headers.get('content-type'));
      
      // Get raw text first to see what's actually returned
      const text = await response.text();
      console.log('Raw response length:', text.length);
      console.log('Raw response:', text);
      
      if (!text || text.trim().length === 0) {
        alert('Server returned empty response. Check if API endpoint exists.');
        return;
      }
      
      // Try to parse it
      let result;
      try {
        result = JSON.parse(text);
      } catch (e) {
        console.error('JSON parse error:', e);
        console.error('Failed to parse:', text);
        alert('Server returned invalid JSON. Response: ' + text.substring(0, 200));
        return;
      }
      
      console.log('Parsed result:', result);
      
      if (result.success) {
        const userData = { ...result.user, token: result.token };
        setUser(userData);
        localStorage.setItem('calibration_user', JSON.stringify(userData));
        localStorage.setItem('calibration_user_token', result.token);
        console.log('Token saved:', result.token);
        setLoginForm({ username: '', password: '' });
        loadData();
      } else {
        alert(result.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('calibration_user');
    setCertificates([]);
    setEquipmentData({});
    setEquipmentTypes([]);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [certsRes, typesRes] = await Promise.all([
        api('/certificates/list.php'),
        api('/equipment/types.php')
      ]);
      
      setCertificates(certsRes.data || []);
      setEquipmentTypes(typesRes.data || []);
      
      // Load equipment data and schemas for each type
      const equipData = {};
      const schemas = {};
      for (const type of typesRes.data || []) {
        const [dataRes, schemaRes] = await Promise.all([
          api(`/equipment/list.php?type=${type}`),
          api(`/equipment/schema.php?type=${type}`)
        ]);
        equipData[type] = dataRes.data || [];
        schemas[type] = schemaRes.fields || [];
      }
      setEquipmentData(equipData);
      setEquipmentSchemas(schemas);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const isRecordLocked = (date) => {
    if (user?.role === 'admin') return false;
    if (!date) return false;
    const recordDate = new Date(date);
    const daysSince = (Date.now() - recordDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSince > LOCK_DAYS;
  };

  const canEdit = (record) => {
    if (user?.role === 'admin') return true;
    return !isRecordLocked(record.cal_date || record.date_received);
  };

  const canDelete = () => user?.role === 'admin';

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getSerialHistory = (serialNo) => {
    const allRecords = [];
    
    certificates.filter(c => c.serial_number === serialNo).forEach(c => {
      allRecords.push({
        type: 'Certificate',
        date: c.cal_date,
        locked: isRecordLocked(c.cal_date),
        data: c
      });
    });
    
    equipmentTypes.forEach(type => {
      (equipmentData[type] || []).filter(r => r.serial_number === serialNo).forEach(r => {
        allRecords.push({
          type: type,
          date: r.cal_date || r.date_received,
          locked: isRecordLocked(r.cal_date || r.date_received),
          data: r
        });
      });
    });
    
    return allRecords.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const uniqueSerials = [...new Set([
    ...certificates.map(c => c.serial_number),
    ...equipmentTypes.flatMap(t => (equipmentData[t] || []).map(r => r.serial_number))
  ])].filter(Boolean).sort();

  const addCertificate = async () => {
    try {
      setLoading(true);
      
      // Calculate expiry date
      let expDate = '';
      if (newCertificate.cal_date) {
        const calDate = new Date(newCertificate.cal_date);
        calDate.setMonth(calDate.getMonth() + parseInt(newCertificate.exp_months));
        calDate.setDate(calDate.getDate() - 1);
        expDate = calDate.toISOString().split('T')[0];
      }
      
      const response = await api('/certificates/create.php', 'POST', {
        tool: newCertificate.tool,
        serial_number: newCertificate.serial_number,
        cal_date: newCertificate.cal_date,
        exp_date: expDate,
        result: newCertificate.result
      });
      
      if (response.success) {
        alert('Certificate created successfully');
        setShowAddCertificate(false);
        setNewCertificate({
          tool: '',
          serial_number: '',
          cal_date: '',
          exp_months: '12',
          result: 'PASS'
        });
        await loadData();
      }
    } catch (error) {
      alert('Failed to create certificate: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateCertificate = async (id, field, value) => {
    try {
      await api('/certificates/update.php', 'POST', {
        id,
        field,
        value
      });
      await loadData();
    } catch (error) {
      alert('Failed to update certificate');
    }
  };

  const deleteCertificate = async (id) => {
    if (!canDelete()) {
      alert('Only administrators can delete records');
      return;
    }
    if (!confirm('Delete this certificate? This cannot be undone.')) return;
    
    try {
      await api('/certificates/delete.php', 'POST', { id });
      await loadData();
    } catch (error) {
      alert('Failed to delete certificate');
    }
  };

  const addEquipmentRecord = async (type) => {
    try {
      setLoading(true);
      const response = await api('/equipment/create.php', 'POST', {
        type,
        customer: newEquipment.customer,
        serial_number: newEquipment.serial_number,
        model: newEquipment.model,
        recal: newEquipment.recal
      });
      if (response.success) {
        alert('Equipment record created successfully');
        setShowAddEquipment(false);
        setNewEquipment({
          customer: '',
          serial_number: '',
          model: 'VKIT-LFM',
          recal: 'Y'
        });
        await loadData();
      }
    } catch (error) {
      alert('Failed to create record: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateEquipmentRecord = async (type, id, field, value) => {
    try {
      await api('/equipment/update.php', 'POST', {
        type,
        id,
        field,
        value
      });
      await loadData();
    } catch (error) {
      alert('Failed to update record');
    }
  };

  const deleteEquipmentRecord = async (type, id) => {
    if (!canDelete()) {
      alert('Only administrators can delete records');
      return;
    }
    if (!confirm('Delete this record? This cannot be undone.')) return;
    
    try {
      await api('/equipment/delete.php', 'POST', { type, id });
      await loadData();
    } catch (error) {
      alert('Failed to delete record');
    }
  };

  const handleCSVImport = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    try {
      const response = await fetch(`${API_URL}/import.php`, {
        method: 'POST',
        headers: { 'Authorization': user?.token || '' },
        body: formData
      });
      
      const result = await response.json();
      if (result.success) {
        alert(`Imported ${result.count} records`);
        await loadData();
      } else {
        alert('Import failed: ' + result.message);
      }
    } catch (error) {
      alert('Import error: ' + error.message);
    }
  };

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]).filter(k => k !== 'id');
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg w-96">
          <div className="flex items-center justify-center mb-6">
            <Shield className="text-blue-600" size={48} />
          </div>
          <h2 className="text-2xl font-bold text-center mb-6">Calibration Tracker</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg"
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                disabled={loading}
              />
            </div>
            <button 
              onClick={handleLogin} 
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              disabled={loading}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 text-white p-6">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          <div>
            <h1 className="text-3xl font-bold">Calibration Certificate Tracker</h1>
            <p className="text-blue-100 mt-1">Asset management and certificate traceability</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium">{user.username}</p>
              <p className="text-xs text-blue-200">{user.role === 'admin' ? 'Administrator' : 'User'}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 bg-blue-700 rounded-lg hover:bg-blue-800">
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search serial numbers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <button 
              onClick={() => setViewMode(viewMode === 'serial' ? 'table' : 'serial')}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              {viewMode === 'serial' ? <History size={20} /> : <Eye size={20} />}
              {viewMode === 'serial' ? 'Table View' : 'Serial View'}
            </button>
            <label className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 cursor-pointer">
              <Upload size={20} /> Import CSV
              <input type="file" accept=".csv" onChange={(e) => handleCSVImport(e, 'certificates')} className="hidden" />
            </label>
          </div>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading...</p>
          </div>
        )}

        {!loading && viewMode === 'serial' && !selectedSerial && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Equipment by Serial Number</h2>
                <p className="text-sm text-gray-600">Click any serial number to view complete calibration history</p>
              </div>
              {user.role === 'admin' && (
                <button 
                  onClick={() => {
                    setShowAddEquipment(true);
                    console.log('Add equipment clicked, modal should show');
                  }} 
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus size={20} /> Add Equipment
                </button>
              )}
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {uniqueSerials
                  .filter(s => !searchTerm || s.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map(serial => {
                    const history = getSerialHistory(serial);
                    const latest = history[0];
                    const hasLocked = history.some(h => h.locked);
                    
                    return (
                      <div 
                        key={serial} 
                        onClick={() => setSelectedSerial(serial)} 
                        className="p-4 border rounded-lg hover:shadow-lg cursor-pointer transition hover:border-blue-400"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-bold text-lg">{serial}</h3>
                          <div className="flex gap-1">
                            {hasLocked && <Lock size={16} className="text-gray-400" />}
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">{history.length}</span>
                          </div>
                        </div>
                        {latest && (
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><span className="font-medium">Last Cal:</span> {formatDate(latest.date)}</p>
                            <p><span className="font-medium">Type:</span> {latest.type}</p>
                            {latest.data.result && (
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${latest.data.result === 'PASS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {latest.data.result}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {!loading && viewMode === 'serial' && selectedSerial && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setSelectedSerial(null)} 
                  className="p-2 hover:bg-gray-100 rounded"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h2 className="text-xl font-bold">Serial Number: {selectedSerial}</h2>
                  <p className="text-sm text-gray-600">Complete calibration history</p>
                </div>
              </div>
              <button 
                onClick={() => exportCSV(getSerialHistory(selectedSerial).map(h => h.data), `${selectedSerial}-history.csv`)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <Download size={20} /> Export
              </button>
            </div>
            
            <div className="p-4">
              <div className="space-y-4">
                {getSerialHistory(selectedSerial).map((record, idx) => (
                  <div key={idx} className={`p-4 border rounded-lg ${record.locked ? 'bg-gray-50' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded font-medium text-sm">
                          {record.type}
                        </span>
                        <span className="text-sm text-gray-600">{formatDate(record.date)}</span>
                        {record.locked && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Lock size={14} /> Locked ({LOCK_DAYS}+ days)
                          </span>
                        )}
                      </div>
                      {record.data.result && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${record.data.result === 'PASS' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {record.data.result}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      {record.type === 'Certificate' ? (
                        <>
                          <div><span className="text-gray-600">Cert No:</span> <span className="font-medium">{record.data.cert_number}</span></div>
                          <div><span className="text-gray-600">Tool:</span> {record.data.tool}</div>
                          <div><span className="text-gray-600">Cal Date:</span> {formatDate(record.data.cal_date)}</div>
                          <div><span className="text-gray-600">Expires:</span> {formatDate(record.data.exp_date)}</div>
                        </>
                      ) : (
                        <>
                          <div><span className="text-gray-600">Customer:</span> {record.data.customer}</div>
                          <div><span className="text-gray-600">Model:</span> {record.data.model}</div>
                          <div><span className="text-gray-600">SOP:</span> {record.data.sop}</div>
                          <div><span className="text-gray-600">Recal:</span> {record.data.recal}</div>
                          <div><span className="text-gray-600">Received:</span> {formatDate(record.data.date_received)}</div>
                          <div><span className="text-gray-600">Dispatched:</span> {formatDate(record.data.date_dispatched)}</div>
                          <div><span className="text-gray-600">Turnaround:</span> {record.data.turnaround}</div>
                          <div><span className="text-gray-600">Cert No:</span> {record.data.cert_number}</div>
                          {record.data.factor_before && (
                            <>
                              <div><span className="text-gray-600">Factor Before:</span> {record.data.factor_before}</div>
                              <div><span className="text-gray-600">Factor After:</span> {record.data.factor_after}</div>
                              <div><span className="text-gray-600">Adjustment:</span> {record.data.adjustment}</div>
                            </>
                          )}
                          {record.data.comments && (
                            <div className="col-span-2"><span className="text-gray-600">Comments:</span> {record.data.comments}</div>
                          )}
                        </>
                      )}
                    </div>
                    
                    {canEdit(record.data) && (
                      <div className="mt-3 pt-3 border-t flex gap-2">
                        <button className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                          <Edit2 size={14} /> Edit
                        </button>
                        {canDelete() && (
                          <button className="text-sm text-red-600 hover:text-red-800 flex items-center gap-1">
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {!loading && viewMode === 'table' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h2 className="text-xl font-bold">Table View</h2>
              <p className="text-sm text-gray-600">Traditional spreadsheet view - Switch to Serial View for better equipment tracking</p>
            </div>
            <div className="p-4 text-center text-gray-500">
              Table view available - Use Serial View for better workflow
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalibrationTracker;